import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { useGestor } from "@/pages/_payments/CarteraCliente";
import { ArrowRightIcon, CheckIcon, XIcon, ClockIcon, CheckCircleIcon, ProhibitIcon } from "@phosphor-icons/react";

const fh = (iso) => (iso ? new Date(iso).toLocaleString("es-HN", { dateStyle: "short", timeStyle: "short" }) : "");
const ESTADO = {
  pendiente: { l: "Pendiente de aprobación", i: ClockIcon, c: "text-agro-yellow border-agro-yellow/40 bg-agro-yellow/10" },
  aplicado: { l: "Aplicado", i: CheckCircleIcon, c: "text-agro-green border-agro-green/40 bg-agro-green/10" },
  rechazado: { l: "Rechazado", i: ProhibitIcon, c: "text-red-300 border-red-500/40 bg-red-500/10" },
};
const cuenta = (c) => (c ? `${c.codigo ? `${c.codigo} · ` : ""}${c.nombre || "Cuenta principal"}` : "—");

/** Bandeja de autorizaciones de CAMBIO DE PROYECTO (distinta de las solicitudes de nota de crédito). */
export default function AutorizacionesTab() {
  const { admin } = useGestor();
  const [vista, setVista] = useState("pendiente");
  const [items, setItems] = useState(null);
  const [rechazo, setRechazo] = useState({});
  const cargar = useCallback(() => {
    setItems(null);
    api.get("/cobranza/cambios-proyecto", { params: { estado: vista === "pendiente" ? "pendiente" : "resueltos" } })
      .then(({ data }) => setItems(data)).catch((e) => { toast.error(apiErr(e)); setItems([]); });
  }, [vista]);
  useEffect(() => { cargar(); }, [cargar]);
  const resolver = async (c, accion) => {
    try {
      await api.post(`/cobranza/cambios-proyecto/${c.id}/resolver`, { accion, motivo: rechazo[c.id] || "" });
      toast.success(accion === "aprobar" ? "Cambio aplicado" : "Solicitud rechazada");
      cargar();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-3" data-testid="autorizaciones">
      <div>
        <h3 className="font-heading text-base font-semibold">Autorizaciones · cambio de proyecto o subcuenta</h3>
        <p className="text-xs text-text-secondary">Las solicitudes de traslado a subcuenta NC están en Ventas y Facturación → Notas de crédito.</p>
      </div>
      <div className="flex gap-1.5" role="group" aria-label="Vista">
        {[["pendiente", "Pendientes"], ["resueltos", "Resueltas"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setVista(k)} aria-pressed={vista === k} data-testid={`aut-vista-${k}`}
            className={`min-h-[40px] px-3 rounded-full border text-xs ${vista === k ? "bg-agro-green/15 border-agro-green/50 text-agro-green font-semibold" : "border-border text-text-secondary"}`}>{l}</button>
        ))}
      </div>
      {!items ? <div className="text-sm text-text-muted">Cargando…</div> : items.length === 0 ? (
        <div className="bg-card border border-border/60 rounded-xl p-8 text-center text-sm text-text-secondary" data-testid="aut-vacio">Sin solicitudes.</div>
      ) : (
        <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {items.map((c) => {
            const e = ESTADO[c.estado] || ESTADO.pendiente;
            const I = e.i;
            return (
              <li key={c.id} className="bg-card border border-border/60 rounded-xl p-4 text-sm" data-testid={`aut-${c.id}`}>
                <div className="flex flex-wrap justify-between gap-2">
                  <div><div className="font-semibold">{c.cliente_nombre}</div><div className="text-xs text-text-muted font-mono">{c.factura_numero} · saldo {fmtL(c.saldo)}</div></div>
                  <span className={`inline-flex items-center gap-1 h-fit text-[11px] font-semibold px-2 py-0.5 rounded border ${e.c}`}><I size={11} /> {e.l}</span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs"><span className="px-2 py-1 rounded bg-app border border-border">{cuenta(c.origen)}</span>
                  <ArrowRightIcon size={14} aria-label="a" /><span className="px-2 py-1 rounded bg-agro-green/10 border border-agro-green/40">{cuenta(c.destino)}</span></div>
                <div className="mt-2 text-xs"><span className="text-text-muted">Motivo:</span> {c.motivo}</div>
                <div className="text-[11px] text-text-muted mt-0.5">Solicitó {c.solicitante_nombre} · {fh(c.fecha)}{c.directo ? " · corrección directa del Administrador" : ""}</div>
                {c.motivo_rechazo && <div className="text-xs text-red-300 mt-1">Rechazo: {c.motivo_rechazo}</div>}
                {admin && c.estado === "pendiente" && (
                  <div className="mt-3 space-y-2">
                    <input value={rechazo[c.id] || ""} onChange={(ev) => setRechazo({ ...rechazo, [c.id]: ev.target.value })} placeholder="Motivo (obligatorio para rechazar)"
                      className="w-full min-h-[44px] bg-app border border-border rounded-md px-3 text-sm" data-testid={`aut-motivo-${c.id}`} />
                    <div className="flex gap-2 justify-end">
                      <button type="button" onClick={() => resolver(c, "rechazar")} disabled={(rechazo[c.id] || "").trim().length < 3} data-testid={`aut-rechazar-${c.id}`}
                        className="min-h-[44px] px-4 border border-red-500/50 text-red-300 rounded-md text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40"><XIcon size={14} /> Rechazar</button>
                      <button type="button" onClick={() => resolver(c, "aprobar")} data-testid={`aut-aprobar-${c.id}`}
                        className="min-h-[44px] px-4 bg-agro-green text-black rounded-md text-xs font-semibold flex items-center gap-1.5"><CheckIcon size={14} weight="bold" /> Aprobar</button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
